import { useTRPC } from "../../../trpc";
import { Card, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useIntl } from "react-intl";
import { TilesetItem } from "./components/TilesetItem";
import { TilesetListControls } from "./components/TilesetListControls";
import { TilesetItemCondensed } from "./components/TilesetItemCondensed";
import { useDebouncedValue, useLocalStorage } from "@mantine/hooks";
import { useState, useMemo, useCallback } from "react";
import { getCurrentVersion } from "./utils/tileset";
import { useQuery } from "@tanstack/react-query";
import type { TilesetWithVersions } from "./types/tileset";

const sortByDate = (a: TilesetWithVersions, b: TilesetWithVersions) => {
  const versionA = getCurrentVersion(a);
  const versionB = getCurrentVersion(b);

  return new Date(versionA?.updatedAt ?? versionA?.createdAt ?? 0).getTime() - new Date(versionB?.updatedAt ?? versionB?.createdAt ?? 0).getTime();
};

function sortTilesets(tilesets: TilesetWithVersions[]): TilesetWithVersions[] {
  const publicTilesets = tilesets.filter(s => s.tileset.scope === "public").sort(sortByDate);
  const privateTilesets = tilesets.filter(s => s.tileset.scope === "private").sort(sortByDate);
  return [...privateTilesets, ...publicTilesets];
}

export function Tilesets() {
  const intl = useIntl();
  const trpc = useTRPC();

  const { data: tilesets = [] } = useQuery(trpc.getTilesetsWithVersions.queryOptions(undefined, {
    refetchInterval: 1000 * 60 * 5,
  }));
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);

  const [isCondensedView, setIsCondensedView] = useLocalStorage({
    key: "tileset-view-mode",
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  const toggleView = useCallback(() => {
    setIsCondensedView(!isCondensedView);
  }, [isCondensedView, setIsCondensedView]);

  const filteredTilesets = useMemo(() => {
    if (!debouncedQuery.trim()) return sortTilesets(tilesets);

    const query = debouncedQuery.toLowerCase().trim();
    const filtered = tilesets.filter((item) => {
      const {
        tileset: { key: tilesetKey },
        dataset,
      } = item;

      if (tilesetKey.toLowerCase().includes(query)) return true;
      if (dataset?.key.toLowerCase().includes(query)) return true;

      return false;
    });
    return sortTilesets(filtered);
  }, [tilesets, debouncedQuery]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Home", id: "navigation.home" })}
        current={intl.formatMessage({ defaultMessage: "Tilesets", id: "navigation.tilesets" })}
        description={intl.formatMessage({ defaultMessage: "View and manage your tilesets. Create new tilesets.", id: "text.tilesetsOverviewDescription" })}
      />

      <TilesetListControls
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isCondensedView={isCondensedView}
        toggleView={toggleView}
      />

      <Stack gap={isCondensedView ? "xs" : "md"}>
        {isCondensedView
          ? (
              filteredTilesets.map((tilesetWithVersions) => {
                const {
                  dataset,
                  tileset,
                  account,
                } = tilesetWithVersions;

                const version = getCurrentVersion(tilesetWithVersions);

                return (
                  <TilesetItemCondensed
                    key={`${account.name}-${dataset?.key}-${tileset.key}`}
                    tileset={tileset}
                    account={account}
                    version={version}
                    dataset={dataset}
                  />
                );
              })
            )
          : (
              filteredTilesets.map((tilesetWithVersions) => {
                const {
                  dataset,
                  tileset,
                  account,
                } = tilesetWithVersions;

                const version = getCurrentVersion(tilesetWithVersions);

                return (
                  <TilesetItem
                    key={`${account.name}-${dataset?.key}-${tileset.key}`}
                    tileset={tileset}
                    account={account}
                    version={version}
                    dataset={dataset}
                  />
                );
              })
            )}

        {filteredTilesets.length === 0 && (
          <Card withBorder p="xl" ta="center">
            {searchQuery
              ? (
                  intl.formatMessage({
                    defaultMessage: "No tilesets found matching: {query}",
                    id: "text.noTilesetsFound",
                  }, { query: searchQuery })
                )
              : (
                  intl.formatMessage({
                    defaultMessage: "No tilesets available. Create your first tileset!",
                    id: "text.noTilesets",
                  })
                )}
          </Card>
        )}
      </Stack>

    </Stack>
  );
}
